// Keyboard extensions (Vimium and its like: Surfingkeys, Tridactyl, cVim,
// Vimium C, Vim Vixen) take keys before any page sees them: their listeners
// are on the window, in the capture phase, from before the page's scripts
// run, and a key they take never reaches the page, its release neither. In
// a text field they let keys through but Esc, which they take and with it
// blur the field: the terminal's own (xterm.js's textarea) loses the keys,
// and what follows is theirs (j, k, f, / …), until a click on the terminal.
// A page can't take the keys back; the visitor can, by excluding the site.
//
// So the page watches for an extension acting on it, and then suggests
// that. Two signs, either enough:
//   - the terminal's textarea blurred with nothing the page saw to blur it:
//     no press of the pointer, no key, the window still focused, and the
//     focus left on the page's body, not on a surface or a control;
//   - Vimium's own UI added to the page (its HUD, its link hints), which
//     it adds only where it is on.
// Which extension it is, the page tells by the stylesheet each puts in
// every page (KNOWN), excluded ones too: that only names it and says what
// to do in it, since being installed is not being on here. Vimium C and
// Vim Vixen put none; the suggestion for them names no one.

const DISMISSED = "hotty-demo.keytakers-dismissed";

/** An extension the page can tell by its stylesheet: an element `tag` of
 * the class `cls` takes its rule, `prop` computed to `value`; and how to
 * exclude this site in it. */
interface Known {
  name: string;
  tag: string;
  cls: string;
  prop: string;
  value: string;
  how: () => string;
}

const KNOWN: Known[] = [
  {
    // content_scripts/vimium.css: its frames, hidden.
    name: "Vimium",
    tag: "iframe",
    cls: "vimium-ui-component-hidden",
    prop: "display",
    value: "none",
    how: () => "click Vimium's icon in the toolbar, then “Exclude Vimium keys on this page”, and save.",
  },
  {
    // content_scripts/content.css: its hints' host takes no pointer.
    name: "Surfingkeys",
    tag: "div",
    cls: "surfingkeys_hints_host",
    prop: "pointer-events",
    value: "none",
    how: () => "press Alt+S: Surfingkeys turns off for this site, and remembers it.",
  },
  {
    // static/css/hint.css: an element its hints killed.
    name: "Tridactyl",
    tag: "div",
    cls: "TridactylKilledElem",
    prop: "display",
    value: "none",
    how: () => `press Esc, then type :blacklistadd ${location.host} and Enter.`,
  },
  {
    // content_scripts/main.css: a hint that does not match.
    name: "cVim",
    tag: "div",
    cls: "cVim-link-hint_match_hidden",
    prop: "display",
    value: "none",
    how: () => `add "${location.origin}/*" to the blacklists in cVim's settings.`,
  },
];

/** The first of KNOWN whose stylesheet is in the page. */
function installed(): Known | undefined {
  for (const k of KNOWN) {
    const probe = document.createElement(k.tag);
    probe.className = k.cls;
    probe.setAttribute("aria-hidden", "true");
    Object.assign(probe.style, { position: "absolute", left: "-9999px", width: "0", height: "0", border: "0" });
    document.body.appendChild(probe);
    const on = getComputedStyle(probe).getPropertyValue(k.prop) === k.value;
    probe.remove();
    if (on) return k;
  }
  return undefined;
}

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED) === "1";
  } catch {
    return false;
  }
}

function dismiss() {
  try {
    localStorage.setItem(DISMISSED, "1");
  } catch {
    // Without storage the note comes back on the next visit.
  }
}

/** The note, with what to do in the extension that is acting, where the
 * page can tell which. */
function note(k: Known | undefined, onClose: () => void): HTMLElement {
  const el = document.createElement("aside");
  el.id = "keytakers";
  el.setAttribute("role", "status");
  const text = document.createElement("p");
  const strong = document.createElement("b");
  strong.textContent = k ? `${k.name} is taking keys here.` : "A keyboard extension is taking keys here.";
  text.append(strong, " ");
  if (k) {
    text.append(`It takes Esc before the terminal sees it, and then its own shortcuts. To give the keys to the terminal, ${k.how()}`);
  } else {
    text.append(
      "One like Vimium C or Vim Vixen took Esc before the terminal saw it. To give the keys to the terminal, " +
        "exclude this site in its settings.",
    );
  }
  const close = document.createElement("button");
  close.type = "button";
  close.textContent = "Got it";
  close.addEventListener("click", onClose);
  el.append(text, close);
  return el;
}

/** Watches the terminal's textarea and the page for an extension acting,
 * and shows the note once; focus is where the terminal is; ownBlur, whether
 * the page itself is blurring the textarea. */
export function watchKeyTakers(textarea: HTMLTextAreaElement, focus: () => void, ownBlur: () => boolean) {
  if (dismissed()) return;
  let shown: HTMLElement | null = null;
  let pressed = 0; // when the pointer was last pressed, or a key seen
  const seen = () => (pressed = performance.now());
  addEventListener("pointerdown", seen, true);
  addEventListener("keydown", seen, true);

  const show = () => {
    if (shown || dismissed()) return;
    shown = note(installed(), () => {
      dismiss();
      shown?.remove();
      focus();
    });
    document.body.appendChild(shown);
    stop();
  };

  // A blur the page caused comes in the same turn as its press or key.
  const onBlur = (e: FocusEvent) => {
    if (e.relatedTarget || ownBlur() || performance.now() - pressed < 50) return;
    // Where the focus went is known once the blur is over.
    setTimeout(() => {
      if (document.hasFocus() && document.activeElement === document.body && !document.hidden) show();
    }, 0);
  };
  textarea.addEventListener("blur", onBlur);

  const ui = new MutationObserver((records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n instanceof HTMLElement && n.classList.contains("vimium-reset")) return show();
      }
    }
  });
  ui.observe(document.documentElement, { childList: true });

  function stop() {
    textarea.removeEventListener("blur", onBlur);
    removeEventListener("pointerdown", seen, true);
    removeEventListener("keydown", seen, true);
    ui.disconnect();
  }
}
